import React, { useState, useEffect } from 'react';
import { X, Play, Clock, Check, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';

export const AssignRecordingModal = ({ isOpen, onClose, childId, childName, onAssignSuccess }) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userSubscription, setUserSubscription] = useState(null);
  const [kidFamilyTier, setKidFamilyTier] = useState('free');

  const [kidFamilyId, setKidFamilyId] = useState(null);

  
  const { showToast, ToastContainer } = useToast();
  
  useEffect(() => {
    if (isOpen) {
      fetchUserSubscription();
      fetchKidFamilyTier();
      fetchMyRecordings();
    }
  }, [isOpen, childId]);


  const fetchUserSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/subscription-status`, {
        credentials: 'include'
      });
      const data = await response.json();
      setUserSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchKidFamilyTier = async () => {
    try {
      // Get kid's family info
      const response = await fetch(`${API_URL}/api/family/my-families`, {
        credentials: 'include'
      });
      const families = await response.json();
      
      // Find the family this kid belongs to
      for (const family of families) {
        const detailRes = await fetch(`${API_URL}/api/family/${family.id}`, {
          credentials: 'include'
        });
        const detail = await detailRes.json();
        
        if (detail.kids?.some(k => k.id === childId)) {
          setKidFamilyId(detail.id);
          // Found the kid's family, now check admin subscription
          for (const member of detail.members || []) {
            if (member.family_role === 'admin') {
              const subRes = await fetch(`${API_URL}/api/stripe/user/${member.id}/status`, {
                credentials: 'include'
              });
              const subData = await subRes.json();
              if (subData.subscription && subData.subscription.status === 'active') {
                setKidFamilyTier(subData.subscription.tier);
                return;
              }
            }
          }
          setKidFamilyTier('free');
          return;
        }
      }
    } catch (error) {
      console.error('Error fetching kid family tier:', error);
      setKidFamilyTier('free');
    }
  };

  const canAssignRecording = (recording) => {
  if (!recording.is_premium) {
    return { canAssign: true, reason: null };
  }

  const userTier = userSubscription?.plan_type || 'free';
  
  // Check if any already-assigned kids are from the same family as the target kid
  const hasKidFromSameFamily = recording.kids?.some(k => k.family_id === kidFamilyId);
  
  // If same family, no token needed regardless of tier
  if (hasKidFromSameFamily) {
    return {
      canAssign: true,
      reason: null  // No token cost - same family that requested it
    };
  }

  // Free grandparent with premium recording - different family
  if (userTier === 'free') {
    return {
      canAssign: false,
      reason: 'Free members can only assign premium stories to the family that requested them. Upgrade to Premium or Gold to share with other families.'
    };
  }

  // Premium grandparent - different family
  if (userTier === 'premium') {
    if (kidFamilyTier === 'free') {
      return {
        canAssign: false,
        reason: 'Premium members can only share premium stories with Premium or Gold families. This family is on the Free plan.'
      };
    }
    return { canAssign: true, reason: 'Will cost 1 share token' };
  }

  // Gold grandparent - different family
  if (userTier === 'gold') {
    return { canAssign: true, reason: 'Will cost 1 share token' };
  }

  return { canAssign: false, reason: 'Unknown subscription status' };
};

  const fetchMyRecordings = async () => {
    setLoading(true);
    try {
      // Fetch regular recordings
      const response = await fetch(`${API_URL}/api/recording/my-recordings`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch recordings');
      
      const regularRecordings = await response.json();
      
      // Fetch personal book recordings
      const personalResponse = await fetch(`${API_URL}/api/personal-books/my-recordings`, {
        credentials: 'include'
      });
      
      let personalRecordings = [];
      if (personalResponse.ok) {
        const personalData = await personalResponse.json();
        // Transform personal recordings to match the expected format
        personalRecordings = personalData.map(rec => ({
          id: `personal_${rec.id}`,
          is_personal: true,
          is_premium: false,
          processing_status: 'ready',
          duration_seconds: rec.duration_seconds,
          book: {
            title: rec.book?.title || 'Untitled Book',
            author: rec.book?.author || 'Unknown',
            cover_image_url: rec.book?.cover_image_url
          },
          kids: rec.kids || [],
          recording_type: rec.recording_type
        }));
      }
      
      // Combine both types and filter for ready recordings
      const allRecordings = [
        ...regularRecordings.filter(r => r.processing_status === 'ready'),
        ...personalRecordings
      ];
      
      setRecordings(allRecordings);
    } catch (error) {
      console.error('Error fetching recordings:', error);
      showToast('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedRecording) return;
    
    setIsAssigning(true);
    try {
      const isPersonal = selectedRecording.id.toString().startsWith('personal_');
      let response;
      
      if (isPersonal) {
        // Extract the actual ID
        const actualId = selectedRecording.id.replace('personal_', '');
        response = await fetch(`${API_URL}/api/personal-books/recording/${actualId}/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ kid_ids: [childId] })
        });
      } else {
        response = await fetch(`${API_URL}/api/recording/${selectedRecording.id}/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ kid_ids: [childId] })
        });
      }
      
      if (!response.ok) throw new Error('Failed to assign recording');
      
      showToast('Recording assigned successfully!');
      if (onAssignSuccess) onAssignSuccess();
      onClose();
    } catch (error) {
      console.error('Error assigning recording:', error);
      showToast('Failed to assign recording');
    } finally {
      setIsAssigning(false);
    }
  };

  const filteredRecordings = recordings.filter(recording => {
    const searchLower = searchTerm.toLowerCase();
    return (
      recording.book?.title.toLowerCase().includes(searchLower) ||
      recording.book?.author.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <ToastContainer />
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        padding: '2rem',
        maxWidth: '700px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
              Assign Story to {childName}
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
              Select a recording from your library
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Search bar */}
        <input
          type="text"
          placeholder="Search by title or author..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            marginBottom: '1rem'
          }}
        />

        {loading ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>Loading your recordings...</p>
        ) : filteredRecordings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
              {searchTerm ? 'No recordings match your search' : 'You haven\'t recorded any stories yet'}
            </p>
            <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
              Record a story first, then you can assign it to {childName}
            </p>
          </div>
        ) : (
          <>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.75rem',
              marginBottom: '1.5rem',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              {filteredRecordings.map(recording => {
                const isSelected = selectedRecording?.id === recording.id;
                const isAlreadyAssigned = recording.kids?.some(k => k.id === childId);
                const isPersonal = recording.is_personal;
                const eligibility = isPersonal 
                  ? { canAssign: true, reason: null }  // Personal recordings can always be assigned
                  : canAssignRecording(recording);
                const canSelect = !isAlreadyAssigned && eligibility.canAssign;
                
                return (
                  <div
                    key={recording.id}
                    onClick={() => canSelect && setSelectedRecording(recording)}
                    style={{
                      padding: '1rem',
                      border: `2px solid ${
                        isAlreadyAssigned ? '#059669' : 
                        isPersonal ? '#10b981' :
                        !eligibility.canAssign ? '#ef4444' :
                        isSelected ? '#9333ea' : '#e5e7eb'
                      }`,
                      borderRadius: '0.75rem',
                      cursor: canSelect ? 'pointer' : 'not-allowed',
                      background: 
                        isAlreadyAssigned ? '#f0fdf4' : 
                        isPersonal ? (isSelected ? '#dcfce7' : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)') :
                        !eligibility.canAssign ? '#fef2f2' :
                        isSelected ? '#faf5ff' : 'white',
                      opacity: !canSelect ? 0.7 : 1,
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    {/* Add Personal Badge */}
                    {isPersonal && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}>
                        📚 PERSONAL
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ flex: 1, paddingRight: isPersonal ? '5rem' : '0' }}>
                        <h4 style={{ 
                          fontWeight: '600', 
                          fontSize: '1.125rem', 
                          marginBottom: '0.25rem', 
                          color: isPersonal ? '#064e3b' : '#1f2937' 
                        }}>
                          {recording.book?.title || 'Unknown Book'}
                          {recording.is_premium && !isPersonal && (
                            <span style={{ 
                              marginLeft: '0.5rem',
                              fontSize: '0.75rem',
                              background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                              color: 'white',
                              padding: '0.125rem 0.375rem',
                              borderRadius: '0.25rem',
                              fontWeight: '700'
                            }}>
                              PREMIUM
                            </span>
                          )}
                        </h4>
                        <p style={{ 
                          fontSize: '0.875rem', 
                          color: isPersonal ? '#047857' : '#6b7280', 
                          marginBottom: '0.5rem' 
                        }}>
                          by {recording.book?.author || 'Unknown'}
                        </p>
                        <div style={{ 
                          display: 'flex', 
                          gap: '1rem', 
                          fontSize: '0.75rem', 
                          color: isPersonal ? '#065f46' : '#9ca3af', 
                          marginBottom: '0.5rem' 
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Clock size={14} />
                            {Math.floor(recording.duration_seconds / 60)}:{(recording.duration_seconds % 60).toString().padStart(2, '0')}
                          </span>
                          {recording.kids && recording.kids.length > 0 && (
                            <span>
                              Assigned to {recording.kids.length} {recording.kids.length === 1 ? 'child' : 'children'}
                            </span>
                          )}
                          {isPersonal && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              {recording.recording_type === 'audio_only' ? '🎙️' : '📹'}
                              {recording.recording_type === 'audio_only' ? 'Audio' : 'Video'}
                            </span>
                          )}
                        </div>
                        
                        {/* Status messages */}
                        {isAlreadyAssigned && (
                          <p style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '600' }}>
                            ✓ Already assigned to {childName}
                          </p>
                        )}
                        
                        {!isPersonal && !isAlreadyAssigned && !eligibility.canAssign && (
                          <p style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: '500' }}>
                            ✗ {eligibility.reason}
                          </p>
                        )}
                        
                        {!isPersonal && !isAlreadyAssigned && eligibility.canAssign && eligibility.reason && (
                          <p style={{ fontSize: '0.75rem', color: '#9333ea', fontWeight: '500' }}>
                            ℹ {eligibility.reason}
                          </p>
                        )}
                      </div>
                      {isAlreadyAssigned && <Check size={24} color="#059669" />}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#e5e7eb',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedRecording || isAssigning}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: selectedRecording && !isAssigning ? '#9333ea' : '#e5e7eb',
                  color: selectedRecording && !isAssigning ? 'white' : '#9ca3af',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: selectedRecording && !isAssigning ? 'pointer' : 'not-allowed'
                }}
              >
                {isAssigning ? 'Assigning...' : 'Assign Story'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};